/* eslint-disable jsx-a11y/alt-text */
import React, { FC, useState, useEffect, useRef } from "react";
import { Box, Grid, TextField, Button, Card, CardContent, Typography, IconButton, Dialog, DialogContent, DialogTitle, DialogActions, CircularProgress, Select, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Upload as UploadIcon, Image as ImageIcon, Close as CloseIcon, Visibility as VisibilityIcon, DragIndicator as DragIcon } from "@mui/icons-material";
import { useMutation, useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AppCSS } from "../../../../components";
import ConfirmationDialog from "../../../../components/dialogue/ConfirmationDialog";
import { CREATE_UPLOAD_URL, CREATE_PROJECT_FILE, DELETE_COC_IMAGE, SAVE_COC_IMAGE_ORDER, GENERATE_COC } from "../../../../apolloClient/operation/mutations/mutation";
import { COC_IMAGES, GET_PROJECT_DETAILS } from "../../../../apolloClient/operation/querys/userQuery";
import { uploadImage } from "../../../../redux/api/common";
import { FileType, CreateProjectFileInput, PayType } from "../../../../redux/types/Project";
import { CreateUploadFileInput } from "../../../../types/graphQlQuery.interface";

interface CertificateGeneratorProps {
  permissions?: boolean;
}

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: "2px dashed #ccc",
  borderRadius: "8px",
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  minHeight: "120px",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(0, 0, 0, 0.02)",
  },
}));

const PhotoGalleryContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  minHeight: "400px",
  maxHeight: "600px",
  overflowY: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const PhotoItem = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "150px",
  height: "150px",
  margin: theme.spacing(1),
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 4,
  right: 4,
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 1)",
  },
}));

const PreviewPhotoItem = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "200px",
  height: "200px",
  margin: theme.spacing(1),
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  cursor: "move",
  border: "2px solid #e0e0e0",
  userSelect: "none",
  WebkitUserSelect: "none",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: AppCSS.GLAS_BLUE,
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    pointerEvents: "none",
  },
}));

const CertificatePreviewContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  padding: theme.spacing(2),
  margin: "0",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  fontFamily: "Arial, sans-serif",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  overflow: "hidden",
}));

export const CertificateGenerator: FC<CertificateGeneratorProps> = ({ permissions }) => {
  const { id } = useParams<{ id: string }>();
  const projectId = Number(id);
  const [selectedBrand, setSelectedBrand] = useState<string>("glas");

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    clientName: "",
    address: "",
    email: "",
    insuranceCompany: "",
    claimNumbers: "",
    insuranceScopeDate: "",
  });

  const [paymentType, setPaymentType] = useState<PayType | null>(null);

  const [photos, setPhotos] = useState({
    production: [] as Array<{ url: string; fileId?: number }>,
    completion: [] as Array<{ url: string; fileId?: number }>,
    supplemental: [] as Array<{ url: string; fileId?: number }>,
  });

  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<{ section: "production" | "completion" | "supplemental"; index: number } | null>(null);
  const [dragActive, setDragActive] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState({
    production: false,
    completion: false,
    supplemental: false,
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<{ fileId: number; url: string; section: "production" | "completion" | "supplemental" } | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const saveOrderTimerRef = useRef<NodeJS.Timeout | null>(null);

  // GraphQL hooks
  const [getUploadUrl] = useMutation(CREATE_UPLOAD_URL);
  const [createProjectFile] = useMutation(CREATE_PROJECT_FILE);
  const [deleteCocImage] = useMutation(DELETE_COC_IMAGE);
  const [saveCoCImageOrder] = useMutation(SAVE_COC_IMAGE_ORDER);
  const [generateCoc] = useMutation(GENERATE_COC);
  const { data: cocImagesData, loading: loadingImages, refetch: refetchImages } = useQuery(COC_IMAGES, {
    variables: { projectId },
    skip: !projectId,
  });
  const { data: projectDetailsData, loading: loadingProjectDetails } = useQuery(GET_PROJECT_DETAILS, {
    variables: { projectId },
    skip: !projectId,
  });

  // Load existing images from backend
  useEffect(() => {
    if (cocImagesData?.cocImages?.sections) {
      const newPhotos = {
        production: [] as Array<{ url: string; fileId?: number }>,
        completion: [] as Array<{ url: string; fileId?: number }>,
        supplemental: [] as Array<{ url: string; fileId?: number }>,
      };

      cocImagesData.cocImages.sections.forEach((section: { type: string; img: Array<{ fileId: number; url: string }> }) => {
        const photos = section.img.map((image) => ({
          url: image.url,
          fileId: image.fileId,
        }));

        if (section.type === "PRODUCTION_PHOTOS") {
          newPhotos.production = photos;
        } else if (section.type === "COMPLETION_PHOTOS") {
          newPhotos.completion = photos;
        } else if (section.type === "SUPPLEMENTAL_PHOTOS") {
          newPhotos.supplemental = photos;
        }
      });

      setPhotos(newPhotos);
    }
  }, [cocImagesData]);

  // Load project details to populate form
  useEffect(() => {
    if (projectDetailsData?.projectDetails) {
      const details = projectDetailsData.projectDetails;
      const customer = details.customerDetails;
      const projectAddress = details.ProjectAddress;
      const insuranceClaim = details.insuranceScope?.insuranceClaim;

      // Set payment type
      setPaymentType(details.PaymentType || null);

      setFormData({
        date: new Date().toISOString().split('T')[0],
        clientName: customer ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim() : "",
        address: projectAddress ? `${projectAddress.street || ""}, ${projectAddress.city || ""}, ${projectAddress.state || ""}, ${projectAddress.zipCode || ""}`.trim() : "",
        email: customer?.email || "",
        insuranceCompany: insuranceClaim?.companyName || "",
        claimNumbers: insuranceClaim?.ClaimNumber || "",
        insuranceScopeDate: insuranceClaim?.ClaimDate || "",
      });
    }
  }, [projectDetailsData]);

  // Auto-populate selectedPhotos with all photos in saved order
  useEffect(() => {
    const allPhotoUrls = [
      ...photos.supplemental.map(p => p.url),
      ...photos.production.map(p => p.url),
      ...photos.completion.map(p => p.url),
    ];
    setSelectedPhotos(allPhotoUrls);
  }, [photos]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveOrderTimerRef.current) {
        clearTimeout(saveOrderTimerRef.current);
      }
    };
  }, []);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const getFileType = (uploadType: "production" | "completion" | "supplemental"): FileType => {
    switch (uploadType) {
      case "production":
        return FileType.PRODUCTION_PHOTOS;
      case "completion":
        return FileType.COMPLETION_PHOTOS;
      case "supplemental":
        return FileType.SUPPLEMENTAL_PHOTOS;
    }
  };

  const uploadSingleImage = async (file: File, type: "production" | "completion" | "supplemental") => {
    try {
      // Step 1: Get signed upload URL
      const input: CreateUploadFileInput = { contentType: file.type };
      const { data: uploadUrlData } = await getUploadUrl({ variables: { input } });

      if (!uploadUrlData?.createUploadUrl) {
        throw new Error("Failed to get upload URL");
      }

      const { url: signedUrl, fileName } = uploadUrlData.createUploadUrl;

      // Step 2: Upload to S3
      const uploadResult = await uploadImage(signedUrl, file);

      if (!uploadResult.success) {
        throw new Error("Failed to upload image to S3");
      }

      // Step 3: Save file metadata to database
      const createFileInput: CreateProjectFileInput = {
        projectId,
        url: fileName,
        type: getFileType(type),
      };

      await createProjectFile({ variables: { input: createFileInput } });

      return { success: true, fileName };
    } catch (error) {
      console.error("Error uploading image:", error);
      return { success: false, error };
    }
  };

  const handleFileUpload = (type: "production" | "completion" | "supplemental") => async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(prev => ({ ...prev, [type]: true }));
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));

    try {
      let successCount = 0;
      let failCount = 0;

      // Upload images sequentially
      for (const file of imageFiles) {
        const result = await uploadSingleImage(file, type);
        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }
      }

      // Refetch images to update the gallery
      await refetchImages();

      // Show toast notification
      if (successCount > 0) {
        toast.success(`${successCount} image(s) uploaded successfully`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} image(s) failed to upload`);
      }
    } catch (error) {
      console.error("Error during upload:", error);
      toast.error("Error uploading images. Please try again.");
    } finally {
      setIsUploading(prev => ({ ...prev, [type]: false }));
      // Clear the input
      event.target.value = "";
    }
  };

  const handleFileDrop = (type: "production" | "completion" | "supplemental") => async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(null);
    
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    setIsUploading(prev => ({ ...prev, [type]: true }));
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));

    try {
      let successCount = 0;
      let failCount = 0;

      // Upload images sequentially
      for (const file of imageFiles) {
        const result = await uploadSingleImage(file, type);
        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }
      }

      // Refetch images to update the gallery
      await refetchImages();

      // Show toast notification
      if (successCount > 0) {
        toast.success(`${successCount} image(s) uploaded successfully`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} image(s) failed to upload`);
      }
    } catch (error) {
      console.error("Error during upload:", error);
      toast.error("Error uploading images. Please try again.");
    } finally {
      setIsUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDragEnter = (type: "production" | "completion" | "supplemental") => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(type);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(null);
  };

  const handleDragOverUpload = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const getPhotoSection = (photoUrl: string): "production" | "completion" | "supplemental" | null => {
    if (photos.production.find(p => p.url === photoUrl)) return "production";
    if (photos.completion.find(p => p.url === photoUrl)) return "completion";
    if (photos.supplemental.find(p => p.url === photoUrl)) return "supplemental";
    return null;
  };

  const getSectionColor = (section: string | null) => {
    switch (section) {
      case "production": return "#4A90E2";
      case "completion": return "#7ED321";
      case "supplemental": return "#F5A623";
      default: return "#ccc";
    }
  };


  const handleDeletePhoto = (photo: { url: string; fileId?: number }, section: "production" | "completion" | "supplemental") => {
    if (!photo.fileId) {
      toast.error("Cannot delete photo: fileId not available");
      return;
    }
    setPhotoToDelete({ fileId: photo.fileId, url: photo.url, section });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!photoToDelete) return;

    try {
      await deleteCocImage({
        variables: {
          fileId: photoToDelete.fileId,
          projectId,
        },
      });

      // Refetch images to update the gallery
      await refetchImages();

      toast.success("Photo deleted successfully");
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error("Failed to delete photo. Please try again.");
    } finally {
      setDeleteConfirmOpen(false);
      setPhotoToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPhotoToDelete(null);
  };

  const handleGeneratePDF = async () => {
    try {
      setIsGenerating(true);

      // Build sections array with images
      const sections = [];

      if (photos.supplemental.length > 0) {
        sections.push({
          type: "SUPPLEMENTAL_PHOTOS",
          title: "Supplemental Work Photos",
          images: photos.supplemental.map(p => ({ url: p.url })),
        });
      }

      if (photos.production.length > 0) {
        sections.push({
          type: "PRODUCTION_PHOTOS",
          title: "Production Photos",
          images: photos.production.map(p => ({ url: p.url })),
        });
      }

      if (photos.completion.length > 0) {
        sections.push({
          type: "COMPLETION_PHOTOS",
          title: "Completion Photos",
          images: photos.completion.map(p => ({ url: p.url })),
        });
      }

      // Format date to mm-dd-yyyy
      const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
      };

      const input = {
        projectId,
        date: formData.date,
        insuranceDate: formatDate(formData.date),
        customerName: formData.clientName,
        address: formData.address,
        email: formData.email,
        // Include insurer and claimNumber only for INSURANCE payment type
        ...(paymentType === PayType.INSURANCE && {
          insurer: formData.insuranceCompany,
          claimNumber: formData.claimNumbers,
        }),
        sections,
        source: selectedBrand,
      };

      const { data } = await generateCoc({
        variables: { input },
      });

      if (data?.generateCertificateOfCompletion) {
        // Open PDF in new tab
        window.open(data.generateCertificateOfCompletion, "_blank");
        toast.success("Certificate generated successfully!");
        // Close preview after successful generation
        setPreviewOpen(false);
      }
    } catch (error) {
      console.error("Error generating certificate:", error);
      toast.error("Failed to generate certificate. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const saveImageOrder = async (photosToSave?: typeof photos) => {
    try {
      // Use passed photos or current state
      const currentPhotos = photosToSave || photos;
      
      // Organize photos by section with fileIds
      const sections: Array<{ type: string; fileIds: number[] }> = [];

      // Get fileIds for each section
      const supplementalFileIds = currentPhotos.supplemental
        .filter(p => p.fileId)
        .map(p => p.fileId!);
      const productionFileIds = currentPhotos.production
        .filter(p => p.fileId)
        .map(p => p.fileId!);
      const completionFileIds = currentPhotos.completion
        .filter(p => p.fileId)
        .map(p => p.fileId!);

      if (supplementalFileIds.length > 0) {
        sections.push({ type: "SUPPLEMENTAL_PHOTOS", fileIds: supplementalFileIds });
      }
      if (productionFileIds.length > 0) {
        sections.push({ type: "PRODUCTION_PHOTOS", fileIds: productionFileIds });
      }
      if (completionFileIds.length > 0) {
        sections.push({ type: "COMPLETION_PHOTOS", fileIds: completionFileIds });
      }

      if (sections.length > 0) {
        await saveCoCImageOrder({
          variables: {
            input: {
              projectId,
              sections,
            },
          },
        });
        console.log("Image order saved successfully");
        
        // Refetch images to get updated order
        await refetchImages();
      }
    } catch (error) {
      console.error("Error saving image order:", error);
    }
  };

  const debouncedSaveOrder = (photosToSave: typeof photos) => {
    // Clear existing timer
    if (saveOrderTimerRef.current) {
      clearTimeout(saveOrderTimerRef.current);
    }

    // Set new timer for 2 seconds
    saveOrderTimerRef.current = setTimeout(() => {
      saveImageOrder(photosToSave);
    }, 2000);
  };

  const handleDragStart = (e: React.DragEvent, section: "production" | "completion" | "supplemental", index: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", index.toString());
    setDraggedIndex({ section, index });
  };

  const handleDragOver = (e: React.DragEvent, section: "production" | "completion" | "supplemental", index: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only allow drop if dragging within the same section
    if (draggedIndex && draggedIndex.section === section) {
      e.dataTransfer.dropEffect = "move";
    } else {
      e.dataTransfer.dropEffect = "none";
    }
  };

  const handleDrop = (e: React.DragEvent, targetSection: "production" | "completion" | "supplemental", dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedIndex || draggedIndex.section !== targetSection || draggedIndex.index === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    // Reorder within the same section
    const sectionPhotos = [...photos[targetSection]];
    const draggedPhoto = sectionPhotos[draggedIndex.index];
    sectionPhotos.splice(draggedIndex.index, 1);
    sectionPhotos.splice(dropIndex, 0, draggedPhoto);

    const updatedPhotos = {
      ...photos,
      [targetSection]: sectionPhotos,
    };

    setPhotos(updatedPhotos);
    
    setDraggedIndex(null);

    // Trigger debounced save with updated photos
    debouncedSaveOrder(updatedPhotos);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const renderUploadBox = (
    title: string,
    type: "production" | "completion" | "supplemental",
    color: string
  ) => {
    const isSectionUploading = isUploading[type];
    const isAnyUploading = isUploading.production || isUploading.completion || isUploading.supplemental;
    
    return (
      <Grid item xs={12}>
        <input
          accept="image/*"
          style={{ display: "none" }}
          id={`upload-${type}`}
          multiple
          type="file"
          onChange={handleFileUpload(type)}
          disabled={isAnyUploading}
        />
        <label htmlFor={`upload-${type}`}>
          <UploadBox
            onDrop={handleFileDrop(type)}
            onDragOver={handleDragOverUpload}
            onDragEnter={handleDragEnter(type)}
            onDragLeave={handleDragLeave}
            sx={{ 
              borderColor: dragActive === type ? color : photos[type].length > 0 ? color : undefined,
              backgroundColor: dragActive === type ? `${color}15` : undefined,
              borderWidth: dragActive === type ? "3px" : "2px",
              opacity: isAnyUploading && !isSectionUploading ? 0.6 : 1,
              cursor: isAnyUploading ? "not-allowed" : "pointer",
              pointerEvents: isAnyUploading ? "none" : "auto",
            }}
          >
            {isSectionUploading ? (
              <CircularProgress size={32} sx={{ color: color, mb: 0.5 }} />
            ) : (
              <UploadIcon sx={{ fontSize: 32, color: color, mb: 0.5 }} />
            )}
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              {title}
            </Typography>
            <Typography variant="caption" color="textSecondary" align="center">
              {isSectionUploading ? "Uploading..." : "Drag & drop or click"}
            </Typography>
            <Typography variant="caption" color="textPrimary" align="center" sx={{ mt: 0.5, fontSize: "11px", fontStyle: "italic", color: "#ff0000" }}>
              (JPG and PNG only)
            </Typography>
            {photos[type].length > 0 && (
              <Typography variant="caption" sx={{ mt: 1, color: color, fontWeight: "600" }}>
                {photos[type].length} photo(s) uploaded
              </Typography>
            )}
          </UploadBox>
        </label>
      </Grid>
    );
  };

  const allPhotos = [...photos.supplemental, ...photos.production, ...photos.completion];

  const inputStyle = {
    padding: "12px 14px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    width: "100%",
    backgroundColor: "#f9fafb",
    color: "#374151",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    fontSize: "14px",
    fontWeight: 500,
    color: "#374151",
  };

  return (
    <Box sx={{ maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ImageIcon sx={{ fontSize: 32, color: AppCSS.GLAS_BLUE }} />
          <Typography variant="h5" fontWeight="600">
            Certificate Generator
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<VisibilityIcon />}
          onClick={() => setPreviewOpen(true)}
          disabled={!permissions || allPhotos.length === 0}
          sx={{
            backgroundColor: AppCSS.GLAS_BLUE,
            "&:hover": { backgroundColor: "#5b93c7" },
            textTransform: "none",
            px: 4,
          }}
        >
          Preview
        </Button>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Create completion certificates with photos
      </Typography>

      <Grid container spacing={3}>
        {/* Left Side - Project Information */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ImageIcon sx={{ mr: 1, color: AppCSS.GLAS_BLUE }} />
                <Typography variant="h6" fontWeight="600">
                  Project Information
                </Typography>
              </Box>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "200px" }}>
                  <label style={labelStyle}>Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={handleInputChange("date")}
                    disabled={!permissions}
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "200px" }}>
                  <label style={labelStyle}>Client Name</label>
                  <input
                    type="text"
                    placeholder="e.g., John Smith"
                    value={formData.clientName}
                    onChange={handleInputChange("clientName")}
                    disabled={!permissions}
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: "1 1 100%", minWidth: "200px" }}>
                  <label style={labelStyle}>Address(es)</label>
                  <textarea
                    placeholder="e.g., 123 Main St, Dallas, TX 75201"
                    value={formData.address}
                    onChange={handleInputChange("address")}
                    disabled={!permissions}
                    rows={2}
                    style={{ ...inputStyle, resize: "vertical" }}
                  />
                </div>
                <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "200px" }}>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="text"
                    placeholder="client@example.com"
                    value={formData.email}
                    onChange={handleInputChange("email")}
                    disabled={!permissions}
                    style={inputStyle}
                  />
                </div>
                {/* Conditionally show insurance fields only for INSURANCE payment type */}
                {paymentType === PayType.INSURANCE && (
                  <>
                    <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "200px" }}>
                      <label style={labelStyle}>Insurance Company</label>
                      <input
                        type="text"
                        placeholder="e.g., State Farm"
                        value={formData.insuranceCompany}
                        onChange={handleInputChange("insuranceCompany")}
                        disabled={!permissions}
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ flex: "1 1 100%", minWidth: "200px" }}>
                      <label style={labelStyle}>Claim Number(s)</label>
                      <input
                        type="text"
                        placeholder="e.g., ABC123456 & DEF789012"
                        value={formData.claimNumbers}
                        onChange={handleInputChange("claimNumbers")}
                        disabled={!permissions}
                        style={inputStyle}
                      />
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </StyledCard>

          {/* Upload Photos Section - Below Project Information */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <UploadIcon sx={{ mr: 1, color: AppCSS.GLAS_BLUE }} />
              <Typography variant="h6" fontWeight="600">
                Upload Photos
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {renderUploadBox("Supplemental Work Photos", "supplemental", "#F5A623")}
              {renderUploadBox("Production Photos", "production", "#4A90E2")}
              {renderUploadBox("Completion Photos", "completion", "#7ED321")}
            </Grid>
          </Box>
        </Grid>

        {/* Right Side - Photo Gallery */}
        <Grid item xs={12} md={6}>
          <StyledCard sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ImageIcon sx={{ mr: 1, color: AppCSS.GLAS_BLUE }} />
                <Typography variant="h6" fontWeight="600">
                  Photo Gallery
                </Typography>
              </Box>

              <PhotoGalleryContainer sx={{ minHeight: "600px", alignItems: (loadingImages || allPhotos.length === 0) ? "center" : "flex-start" }}>
                {loadingImages ? (
                  <Box sx={{ textAlign: "center" }}>
                    <CircularProgress sx={{ color: AppCSS.GLAS_BLUE }} />
                    <Typography variant="body2" sx={{ mt: 2 }}>Loading photos...</Typography>
                  </Box>
                ) : allPhotos.length === 0 ? (
                  <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                    <ImageIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2">No photos uploaded yet</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
                    {allPhotos.map((photo, index) => {
                      const section = getPhotoSection(photo.url);
                      const sectionColor = getSectionColor(section);
                      return (
                        <PhotoItem
                          key={`${photo.fileId}-${index}`}
                          sx={{
                            border: `3px solid ${sectionColor}`,
                            position: "relative",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <img src={photo.url} alt={`Photo ${index + 1}`} />
                          {photo.fileId && (
                            <DeleteButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (section) handleDeletePhoto(photo, section);
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </DeleteButton>
                          )}
                        </PhotoItem>
                      );
                    })}
                  </Box>
                )}
              </PhotoGalleryContainer>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Preview Modal */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" fontWeight="600">
              Certificate Preview
            </Typography>
            <IconButton onClick={() => setPreviewOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ overflow: "hidden" }}>
          <Grid container spacing={3}>
            {/* Certificate Preview */}
            <Grid item xs={12} md={7} sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Certificate Preview
              </Typography>
              <Box sx={{ maxHeight: "70vh", overflowY: "auto", overflowX: "hidden", pr: 1 }}>
                <CertificatePreviewContainer>
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography variant="h5" fontWeight="700" sx={{ mb: 1, wordBreak: "break-word" }}>
                    CERTIFICATE OF COMPLETION
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, wordBreak: "break-word" }}>
                    <strong>DATE:</strong> {formData.date || "Not specified"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, wordBreak: "break-word" }}>
                    <strong>CUSTOMER NAME:</strong> {formData.clientName || "Not specified"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, wordBreak: "break-word" }}>
                    <strong>ADDRESS:</strong> {formData.address || "Not specified"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, wordBreak: "break-word" }}>
                    <strong>EMAIL:</strong> {formData.email || "Not specified"}
                  </Typography>
                  {/* Conditionally show INSURER and CLAIM # only for INSURANCE payment type */}
                  {paymentType === PayType.INSURANCE && (
                    <>
                      <Typography variant="body2" sx={{ mb: 1, wordBreak: "break-word" }}>
                        <strong>INSURER:</strong> {formData.insuranceCompany || "Not specified"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, wordBreak: "break-word" }}>
                        <strong>CLAIM #:</strong> {formData.claimNumbers || "Not specified"}
                      </Typography>
                    </>
                  )}
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    To whom it may concern:
                  </Typography>
                  <Typography variant="body2" className="text-wrap" sx={{ mb: 2, textAlign: "justify", wordBreak: "break-word" }}>
                    {paymentType === PayType.INSURANCE ? (
                      <>
                        All the work in relation to the scope sent from {formData.insuranceCompany || "[Insurance Company]"} dated{" "}
                        {formData.insuranceScopeDate || "[Date]"} has been completed on the above-referenced CLAIM #{" "}
                        <strong>{formData.claimNumbers || "[Claim Number]"}</strong>. We are requesting the depreciation be sent at this time.
                      </>
                    ) : (
                      <>
                        All the work has been completed as of {formData.date || "[Date]"}. We are requesting the depreciation be sent at this time.
                      </>
                    )}
                  </Typography>
                  <Typography variant="body2" className="text-wrap" sx={{ mb: 2, textAlign: "justify", wordBreak: "break-word" }}>
                    If you would let me know this has occurred by replying to this email, we would greatly appreciate it.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 3 }}>
                    Sincerely,
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2, fontStyle: "italic" }}>
                    /S/ {formData.clientName || "[Client Name]"}
                  </Typography>
                  <Typography variant="body2">{formData.clientName || "[Client Name]"}</Typography>
                </Box>

                {selectedPhotos.length > 0 && (
                  <Box sx={{ mt: 4, pt: 4, borderTop: "1px solid #000" }}>
                    {/* Supplemental Work Photos Section */}
                    {selectedPhotos.filter(photo => getPhotoSection(photo) === "supplemental").length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" fontWeight="600" sx={{ mb: 3, textAlign: "center", color: "#F5A623" }}>
                          Supplemental Work Photos
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                          {selectedPhotos.filter(photo => getPhotoSection(photo) === "supplemental").map((photo, index, arr) => (
                            <Box key={index}>
                              <Box
                                sx={{
                                  width: "100%",
                                  maxWidth: "350px",
                                  aspectRatio: "4/3",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                  border: "3px solid #F5A623",
                                }}
                              >
                                <img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              </Box>
                              {index % 2 === 1 && index < arr.length - 1 && (
                                <Box sx={{ my: 3, textAlign: "center" }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ 
                                    display: "inline-block", 
                                    px: 2, 
                                    py: 0.5, 
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: "4px",
                                    fontStyle: "italic"
                                  }}>
                                    — Page Break —
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Production Photos Section */}
                    {selectedPhotos.filter(photo => getPhotoSection(photo) === "production").length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" fontWeight="600" sx={{ mb: 3, textAlign: "center", color: "#4A90E2" }}>
                          Production Photos
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                          {selectedPhotos.filter(photo => getPhotoSection(photo) === "production").map((photo, index, arr) => (
                            <Box key={index}>
                              <Box
                                sx={{
                                  width: "100%",
                                  maxWidth: "350px",
                                  aspectRatio: "4/3",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                  border: "3px solid #4A90E2",
                                }}
                              >
                                <img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              </Box>
                              {index % 2 === 1 && index < arr.length - 1 && (
                                <Box sx={{ my: 3, textAlign: "center" }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ 
                                    display: "inline-block", 
                                    px: 2, 
                                    py: 0.5, 
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: "4px",
                                    fontStyle: "italic"
                                  }}>
                                    — Page Break —
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Completion Photos Section */}
                    {selectedPhotos.filter(photo => getPhotoSection(photo) === "completion").length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" fontWeight="600" sx={{ mb: 3, textAlign: "center", color: "#7ED321" }}>
                          Completion Photos
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                          {selectedPhotos.filter(photo => getPhotoSection(photo) === "completion").map((photo, index, arr) => (
                            <Box key={index}>
                              <Box
                                sx={{
                                  width: "100%",
                                  maxWidth: "350px",
                                  aspectRatio: "4/3",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                  border: "3px solid #7ED321",
                                }}
                              >
                                <img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              </Box>
                              {index % 2 === 1 && index < arr.length - 1 && (
                                <Box sx={{ my: 3, textAlign: "center" }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ 
                                    display: "inline-block", 
                                    px: 2, 
                                    py: 0.5, 
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: "4px",
                                    fontStyle: "italic"
                                  }}>
                                    — Page Break —
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </CertificatePreviewContainer>
              </Box>
            </Grid>

            {/* Photo Arrangement */}
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Drag to Rearrange Photos
              </Typography>
              <Box sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: "8px", minHeight: "400px", maxHeight: "70vh", overflowY: "auto" }}>
                {selectedPhotos.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                    <ImageIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2">No photos selected</Typography>
                  </Box>
                ) : (
                  <Box>
                    {/* Supplemental Photos */}
                    {photos.supplemental.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: "#F5A623", fontWeight: "600" }}>
                          Supplemental Work Photos ({photos.supplemental.length})
                        </Typography>
                        {photos.supplemental.map((photo, index) => (
                          <PreviewPhotoItem
                            key={`supp-${photo.fileId}-${index}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, "supplemental", index)}
                            onDragOver={(e) => handleDragOver(e, "supplemental", index)}
                            onDrop={(e) => handleDrop(e, "supplemental", index)}
                            onDragEnd={handleDragEnd}
                            sx={{
                              width: "100%",
                              height: "100px",
                              mb: 1,
                              display: "flex",
                              alignItems: "center",
                              opacity: draggedIndex?.section === "supplemental" && draggedIndex?.index === index ? 0.3 : 1,
                              transform: draggedIndex?.section === "supplemental" && draggedIndex?.index === index ? "scale(0.95)" : "scale(1)",
                              border: "2px solid #F5A623",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", width: "100%", p: 1 }}>
                              <DragIcon sx={{ mr: 1, color: "#F5A623" }} />
                              <img
                                src={photo.url}
                                style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "4px" }}
                              />
                              <Box sx={{ ml: 2, flex: 1 }}>
                                <Typography variant="body2" fontWeight="600">
                                  Photo {index + 1}
                                </Typography>
                              </Box>
                            </Box>
                          </PreviewPhotoItem>
                        ))}
                      </Box>
                    )}

                    {/* Production Photos */}
                    {photos.production.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: "#4A90E2", fontWeight: "600" }}>
                          Production Photos ({photos.production.length})
                        </Typography>
                        {photos.production.map((photo, index) => (
                          <PreviewPhotoItem
                            key={`prod-${photo.fileId}-${index}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, "production", index)}
                            onDragOver={(e) => handleDragOver(e, "production", index)}
                            onDrop={(e) => handleDrop(e, "production", index)}
                            onDragEnd={handleDragEnd}
                            sx={{
                              width: "100%",
                              height: "100px",
                              mb: 1,
                              display: "flex",
                              alignItems: "center",
                              opacity: draggedIndex?.section === "production" && draggedIndex?.index === index ? 0.3 : 1,
                              transform: draggedIndex?.section === "production" && draggedIndex?.index === index ? "scale(0.95)" : "scale(1)",
                              border: "2px solid #4A90E2",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", width: "100%", p: 1 }}>
                              <DragIcon sx={{ mr: 1, color: "#4A90E2" }} />
                              <img
                                src={photo.url}
                                style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "4px" }}
                              />
                              <Box sx={{ ml: 2, flex: 1 }}>
                                <Typography variant="body2" fontWeight="600">
                                  Photo {index + 1}
                                </Typography>
                              </Box>
                            </Box>
                          </PreviewPhotoItem>
                        ))}
                      </Box>
                    )}

                    {/* Completion Photos */}
                    {photos.completion.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: "#7ED321", fontWeight: "600" }}>
                          Completion Photos ({photos.completion.length})
                        </Typography>
                        {photos.completion.map((photo, index) => (
                          <PreviewPhotoItem
                            key={`comp-${photo.fileId}-${index}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, "completion", index)}
                            onDragOver={(e) => handleDragOver(e, "completion", index)}
                            onDrop={(e) => handleDrop(e, "completion", index)}
                            onDragEnd={handleDragEnd}
                            sx={{
                              width: "100%",
                              height: "100px",
                              mb: 1,
                              display: "flex",
                              alignItems: "center",
                              opacity: draggedIndex?.section === "completion" && draggedIndex?.index === index ? 0.3 : 1,
                              transform: draggedIndex?.section === "completion" && draggedIndex?.index === index ? "scale(0.95)" : "scale(1)",
                              border: "2px solid #7ED321",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", width: "100%", p: 1 }}>
                              <DragIcon sx={{ mr: 1, color: "#7ED321" }} />
                              <img
                                src={photo.url}
                                style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "4px" }}
                              />
                              <Box sx={{ ml: 2, flex: 1 }}>
                                <Typography variant="body2" fontWeight="600">
                                  Photo {index + 1}
                                </Typography>
                              </Box>
                            </Box>
                          </PreviewPhotoItem>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
              Brand Styling:
            </Typography>
            <Select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              size="small"
              sx={{ minWidth: 120, height: 36 }}
            >
              <MenuItem value="glas">Glas</MenuItem>
              <MenuItem value="waybe">Waybe</MenuItem>
            </Select>
          </Box>
          <Box>
            <Button onClick={() => setPreviewOpen(false)} sx={{ mr: 1 }}>Close</Button>
            <Button
              variant="contained"
              onClick={handleGeneratePDF}
              disabled={!permissions || isGenerating}
              sx={{
                backgroundColor: AppCSS.GLAS_BLUE,
                "&:hover": { backgroundColor: "#5b93c7" },
              }}
            >
              {isGenerating ? (
                <>
                  <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                  Generating...
                </>
              ) : (
                "Generate PDF"
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        title="Delete Photo"
        message="Are you sure you want to delete this photo? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
};
